// Contrat API de l'Observatoire (backend/app/observatoire/router.py) — gelé Sprint A.
export interface Palier { label: string; prix_annuel_ht: number | null }
export interface CommuneMeta {
  code: string; nom: string; population: number | null; palier: Palier
  rattachement?: string | null  // code INSEE de la commune-mère (arrondissement PLM) — décision 26/07
  // > 500 000 hab (GO Marc 27/07) : pas de tunnel libre-service, CTA « Contactez-nous ».
  contact_commercial?: boolean
  // identité étendue — servie par /synthese uniquement (héros maquette), fail-soft
  code_postal?: string | null; epci?: string | null; epci_siren?: string | null
  // CDC EPT (29/10, § 4.1) : l'établissement public territorial du Grand Paris de la commune,
  // en plus de la MGP. Absents pour une commune sans EPT (Paris, toute commune hors MGP).
  ept?: string | null; ept_siren?: string | null
  // #665 : ce qu'est `population` — notion et millésime, lus dans les données par le serveur
  // (« population légale 2023, en vigueur au 1er janvier 2026 »). Absent d'un serveur antérieur.
  population_reference?: PopulationReference | null
}
export interface PopulationReference {
  notion: string | null; millesime: number | null; en_vigueur_au: string | null
  libelle: string | null; libelle_court: string | null
}
export interface SectionSommaire { id: string; num: number; title: string; available: boolean }
export interface ThemeMeta {
  id: string; label: string; color: string; sections: SectionSommaire[]
  // A20 (08/10) : la famille de la thématique — `null` (ou absente, serveur antérieur)
  // tant que l'arborescence à 20 thématiques est éteinte (`ARBORESCENCE_V2=0`).
  // 14/10/2026 (décision de Marc et Cyrille) : MÉTADONNÉE INTERNE, plus jamais affichée —
  // l'interface ne parle que de thématiques et de sections.
  famille?: string | null
}
// A20 : une famille de thématiques (une question d'élu, « Qui vit ici ? »). Retirée de
// l'interface de l'observatoire le 14/10/2026 ; le serveur la sert encore, et seules les pages
// internes de l'administration (maquettes de design) la lisent, par `structure.ts`.
export interface FamilleMeta { id: string; titre: string }
export type AccessLevel = 'public' | 'free' | 'subscriber'
export interface ObsMeta {
  commune: CommuneMeta; access: AccessLevel; is_reference: boolean; themes: ThemeMeta[]
  // A20 (contrat § 5) — TOUS optionnels : un serveur antérieur ne les sert pas, et l'écran
  // doit alors se comporter exactement comme avant (liste plate, nombre = `themes.length`).
  // `nb_themes` est le nombre de thématiques du PRODUIT, pas celui que ce compte voit : un
  // agent restreint reçoit moins de `themes`, la vitrine annonce toujours le total.
  arborescence?: 1 | 2
  nb_themes?: number
  familles?: FamilleMeta[]   // vide en v1 ; jamais affichée (14/10/2026), voir `FamilleMeta`
  // Historique territorial (issue #41) — null pour l'immense majorité des communes, dont le
  // périmètre n'a jamais bougé : le bandeau ne s'affiche alors pas du tout.
  historique_territorial?: {
    // `consigne_redaction` n'est PAS exposée : c'est une instruction au LLM, pas du
    // contenu (voir `_historique_public` côté serveur).
    resume_detaille: string
    fusions: { date: string; communes: string[] }[]
    scissions: { date: string; communes: string[] }[]
    regroupements_anciens: number
  } | null
  viewer?: { commune_role: string | null; allowed_themes: string[] | null } | null
  // T5 : drapeaux de surface servis par le backend (cartes derrière drapeau, 12ᵉ thématique)
  features?: { carte_epci?: boolean; carte_ecoles?: boolean; theme_equ?: boolean
              // Fiche I (06/10) : « Écrire à TerObs » — `COMMENTAIRES_TEROBS`, allumé par défaut
              commentaires_terobs?: boolean }
  // Décisions Marc 08/08 (couverture.py) — des faits de PAGE : l'histoire d'un village
  // détruit (une note, pas trente tirets) et le périmètre commercial (DROM servis,
  // non vendus — le bandeau explique, il n'excuse aucune absence).
  note_commune?: string | null
  hors_perimetre_commercial?: boolean
  bandeau_perimetre_commercial?: string | null
  // Commune DISSOUTE (option A tranchée par Marc, 08/08) : elle n'a plus d'état
  // courant, donc ni chiffre ni onglet — une notice et un lien vers la commune qui
  // l'a absorbée. `code_apres` peut être null : on ne lui invente pas de successeur.
  commune_dissoute?: {
    code_apres: string | null
    libelle_apres: string | null
    date_effet: string | null
    phrase: string
  } | null
}

// Comparaison multi-échelles d'un KPI (priorité A, 28/07) : EPCI, département, région,
// France métropolitaine. `part_commune_pct` n'est servi que pour les STOCKS — un total de
// périmètre ne se compare pas à une commune, son POIDS dans ce périmètre si.
export interface EchelleKpi {
  echelle: string; libelle: string; valeur: number | null; unite?: string
  raison_nd?: string | null; ecart_commune?: number | null
  part_commune_pct?: number | null; note_methode?: string | null
  // C2 : la ligne porte un ÉCART, pas un niveau — elle se rend donc signée des deux
  // côtés. Sans ça un écart positif sortait nu (« 2,9 ») et le lecteur ne pouvait pas
  // savoir de quel côté de l'objectif il se trouvait.
  signe?: boolean
  couverture_population_pct?: number | null
  // Part des COMMUNES du périmètre réellement renseignées. Affichée au client depuis
  // l'arbitrage de Marc du 02/08 (« aligne le site public aussi ») : une comparaison
  // d'échelle est un agrégat que NOUS calculons, pas une statistique publiée — le lecteur
  // doit pouvoir voir sur combien de communes elle repose.
  couverture_communes_pct?: number | null
  // Mention COURTE propre à une échelle, rendue à côté du libellé comme la couverture
  // (24/08). Née de la légende SRU répétée 3-4 fois : la phrase de méthode portait le
  // nombre de communes du périmètre, donc changeait à chaque échelle et échappait au
  // dédoublonnage. La méthode invariante reste dans `note_methode` — qui se dédoublonne —
  // et ce qui VARIE d'une échelle à l'autre se dit ici, au plus près de sa valeur.
  mention_ligne?: string | null
  // `insee_publie` = la valeur supra-communale n'est PAS notre agrégat : c'est le chiffre
  // publié par l'INSEE pour ce périmètre (mécanisme des ancres Filosofi, audit du 30/07 —
  // reconstituer donnait 16,9 % de pauvreté France contre 14,9 % publié). L'attribution
  // affichée doit donc dire l'inverse de celle d'un agrégat maison, et la couverture y est
  // volontairement nulle : elle décrirait un calcul qui n'a pas eu lieu.
  origine?: string | null
  // Rôle générique du périmètre (« Son département ») depuis que `libelle` porte le NOM
  // réel (« Essonne ») — demande Marc 06/08. Servi en infobulle : la pédagogie au survol.
  role?: string | null
  // Socle (26/09) : `origine: 'producteur_publie'` marque une valeur PUBLIÉE par le
  // producteur à ce périmètre, dont le nom vient ici (le socle en compte des dizaines,
  // la table `NOMS_PRODUCTEUR` de `comparaison.ts` n'en connaît que trois).
  producteur?: string | null
}
// Commune TÉMOIN d'un KPI (« les jetons », arbitrage Marc 28/07) : jusqu'à 3 communes
// choisies par le client PLUS celles que TerObs propose. `origine` distingue les deux —
// l'élu doit savoir ce qu'il a demandé et ce que nous avons ajouté.
export interface TemoinKpi {
  code: string; nom: string; origine: 'choisi' | 'propose'
  valeur: number | null; ecart_commune?: number | null; unite?: string
  // Pourquoi la valeur manque (tuiles du socle, lot T3 du 10/10) : « secret statistique »,
  // « millésime absent… » — les mots de `EchelleKpi.raison_nd`. Affiché depuis l'étape 8 du
  // CDC « témoins partout » : « Arpajon : N/D » au survol de la tuile et en note de l'image
  // partagée. `null` toléré : le serveur ne pose la clé que lorsque la valeur manque.
  raison_nd?: string | null
}
export interface Kpi {
  parc_crous?: {
    residences: import('./crous').ResidenceCrous[]
  }
  label: string; value?: number | string | null; unit?: string; note?: string
  certif?: boolean; def_term?: string; gated?: boolean; fiche?: string
  decimales?: number  // précision d'affichage imposée par le backend (taux votés — 06/08)
  // CDC GBM § 3.3 (PX-4) : la limite écrite SOUS le chiffre (neuf / ancien), à part de la note.
  limite?: string
  comparaison?: EchelleKpi[]
  temoins?: TemoinKpi[]
  // 'socle' : tuile servie par le socle des indicateurs (26/09). L'écran l'ignore ; le
  // backend s'en sert pour la tenir hors des lectures rédigées.
  origine?: string
  // Vue EPCI : d'où vient le chiffre (la note le dit en clair) — notre agrégat des communes
  // membres, avec sa couverture [renseignées, membres], ou le chiffre du producteur.
  provenance?: 'communes' | 'producteur'
  couverture?: [number, number]
  // Vue EPCI, tuile du socle : l'année de la valeur montrée (`socle/vue._tuile`). Le menu
  // « Communes membres » lit ses communes cette année-là (revue codex P2 de la PR #721).
  annee?: number | null
  secondaire?: { value: number | null; unit: string; comparaison?: EchelleKpi[]; temoins?: TemoinKpi[] }
}
// Sources officielles d'une section (fiches techniques 13/07) — pied de bloc + annexe PDF
export interface SourceDetail { nom: string; url?: string; cadence?: string }
// Une ligne du tableau d'état (issue #604, `socle/vue.py` `_tableau_etat`) : une présence de
// la commune (Oui / Non, null = non renseigné), avec la note, la fiche et le terme du
// glossaire que portait sa tuile — le (i) de la ligne ouvre le même panneau.
export interface LigneEtat {
  label: string
  valeur: 'Oui' | 'Non' | null
  millesime?: number | string | null
  note?: string
  fiche?: string
  def_term?: string
}
export interface ChartSpec {
  unit?: string  // suffixe axe Y + tooltips (ex. '‰') — lisibilité 16/07
  // 'etat' (#604) : un TABLEAU, rendu par `TableauEtat`, jamais par Chart.js ; il ne porte
  // ni `labels` ni `datasets` côté serveur, seulement `lignes`.
  type: 'bar' | 'line' | 'doughnut' | 'pyramid' | 'etat'
  lignes?: LigneEtat[]
  labels: (string | number)[]
  // abs/absLabel : valeurs ABSOLUES alignées sur data, servies dans l'infobulle
  // (doctrine A1, 12/08 : « proportion à l'écran, valeur absolue en info-bulle »).
  datasets: { label: string; data: (number | null)[]; abs?: (number | null)[]
              absLabel?: string }[]
  empile?: boolean      // barres empilées 100 % (dem-4, 03/08)
  horizontal?: boolean  // barres horizontales hors pyramide
  histo_comparable?: boolean                       // courbe dem-1 : curseur + communes (#112)
  histo_graphe?: string
  histo_unite?: string
  moyenne_mobile?: boolean
  annee_partielle?: number  // année en cours, incomplète : exclue de la moyenne mobile (19/10)
  // Empilés : « + communes » (#112). Resynchronisé le 12/08 : le backend émet une
  // dizaine de clés (registres EMPILES_*), l'union littérale d'ici était en retard
  // de huit valeurs — le contrat de vérité est côté serveur (422 sur clé inconnue).
  barres_comparables?: string
}
export interface SectionChart {
  // `id` (partage par lien, CDC E2 § 14) : l'identifiant stable `<section>.<clé>` posé par le
  // collecteur — c'est lui, et lui seul, que le navigateur envoie pour créer un lien.
  id?: string
  title: string; chart: ChartSpec | null; note?: string; gated?: boolean; fiche?: string
  origine?: string  // 'socle' — voir `Kpi.origine`
}
export interface CommentaireLlm {
  texte?: string; gated?: boolean; status?: string; reason?: 'role'
  created_at?: string; donnees_changees?: boolean
}
// NB : le champ chart.unit ('‰'…) transite dans SectionChart.chart (spec ChartBlock).

export interface Section {
  id: string; num: number; title: string
  // 'hors_champ_source' (décision Marc 1, 08/08) : le territoire est HORS DU CHAMP
  // d'une source de la section — `motif` (champ existant) nomme la source.
  status?: 'ok' | 'ingestion_pending' | 'non_applicable' | 'hors_champ_source'
  gated?: boolean
  reason?: 'tier' | 'role'
  kpis?: Kpi[]
  charts?: SectionChart[]
  sources_attendues?: string[]
  sources_detail?: SourceDetail[]
  // Lot P (15/09) : la fiche technique de la CARTE de cette section. Les cartes sont
  // injectées par le front et n'ont donc pas de `kpi(fiche=…)` où s'accrocher — c'est ce
  // champ qui relie une carte au catalogue de fiches, comme `sources_detail` la relie au
  // registre des sources.
  fiche_carte?: string
  commentaire_llm?: CommentaireLlm
  motif?: string | null
  // Section construite du socle seul (26/09) : `origine: 'socle'`, sans lecture rédigée ;
  // `statut_builder` dit ce qu'elle serait sans lui (en attente, hors du champ).
  origine?: string
  statut_builder?: 'ingestion_pending' | 'hors_champ_source'
  // Vue EPCI (26/09) : les indicateurs placés dans la section qui n'existent pas à
  // l'échelle intercommunale (médiane, décile, taux publié à la seule commune) — NOMMÉS,
  // jamais remplacés par un calcul.
  hors_niveau?: string[]
}
export interface ThemePayload {
  analyse_ensemble?: CommentaireLlm | null
  /** Empreinte des données de CETTE page (14/10) : le sondage des lectures la renvoie, et ne
      reçoit que les lectures écrites sur ces données-là. */
  empreinte_lectures?: string | null
  theme: { id: string; label: string; color: string }
  access: AccessLevel
  commune: CommuneMeta
  sections: Section[]
  // Lot V : pourquoi une lecture n'arrivera pas, dit dès le PREMIER rendu — sans lui,
  // l'écran démarrait un compte à rebours pendant 5 s (le temps du premier sondage) sur
  // une génération déjà coupée. `null` quand rien n'est en attente.
  generation?: { etat?: 'en_cours' | 'credit_epuise' | 'hors_espace' | 'plafond_atteint'
                 message?: string } | null
  // Vue EPCI (26/09, `GET /observatoire/epci/{siren}/theme/{theme}`) : le territoire servi,
  // et `socle_indisponible` quand les tables du socle manquent (la page le dit, rien n'est
  // estimé à la place).
  territoire?: { niveau: string; code: string; nom: string }
  socle_indisponible?: boolean
  // Vue EPCI (audit du 30/09, `collector.chapeau_epci`) : le chapeau de la page, tiré des tuiles
  // servies — il ne revendique l'agrégation des communes que pour ce qui en est une, et nomme les
  // sections qui portent des valeurs du producteur pour l'intercommunalité elle-même.
  chapeau?: { texte: string; valeurs_communes: number; valeurs_producteur: number
              sections_producteur: string[] }
}
// Le classement des écarts — l'organe de TRI de la page de synthèse (conception 09/08).
// Calculé en Python, déterministe : le front n'ordonne rien et ne calcule aucun écart.
export interface LigneEcart {
  theme: string; section: string; label: string
  valeur: number; unite: string
  echelle: string; echelle_libelle: string; valeur_echelle: number
  ecart_affiche: number; ecart_unite: string
  sens: 'au-dessus' | 'au-dessous'
}
export interface ClassementEchelle {
  echelle: string
  // ⚠️ Trois verdicts, jamais deux : un tableau vide veut dire DEUX choses opposées.
  // « conforme » = tout a été comparé et rien ne s'écarte (c'est une information) ;
  // « non_comparable » = rien n'a pu être confronté (c'est un aveu sur nous).
  verdict: 'ecarts' | 'conforme' | 'non_comparable'
  au_dessus: LigneEcart[]; au_dessous: LigneEcart[]
  examines: number; comparables: number; retenus: number
  ecartes: Record<string, number>
}
export interface Classement {
  defaut: string
  // « tuiles » = le tri des comparaisons déjà sur la page (repli permanent) ;
  // « capture » = le tri large calculé pendant la chauffe. Les deux n'examinent pas le
  // même nombre d'indicateurs : leur pied de tableau ne dit donc pas la même chose.
  source: 'tuiles' | 'capture'
  par_echelle: Record<string, ClassementEchelle>
}
export interface SynthesePayload {
  commune: CommuneMeta; access: AccessLevel
  tuiles: Kpi[]
  classement?: Classement
  // Servi ici EN PLUS de /meta : PrintView (le PDF) n'interroge que /synthese.
  historique_territorial?: { resume_detaille: string } | null
  // Même raison : le PDF n'a que /synthese, et c'est son lecteur qui a le PLUS besoin
  // de la notice de dissolution — il n'a pas le site sous la main.
  commune_dissoute?: {
    code_apres: string | null
    libelle_apres: string | null
    date_effet: string | null
    phrase: string
  } | null
  // chiffre phare par carte thématique — tiéré free+ (absent en public), fail-soft par thème
  themes_apercu?: Record<string, { value: number; unit: string; label: string }>
  evolution: SectionChart
  synthese_llm: CommentaireLlm
}

export const API = (import.meta.env.VITE_API_URL || '') + '/api'
